<script>
import { v4 as uuidv4 } from "uuid";
import { environment } from "@app/store/envStore.svelte.js";
import { uiState } from "@app/store/uiStore.svelte.js";
import {
    modalState,
    hideModal,
    showModal,
} from "@app/store/modalStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import { fade } from "svelte/transition";
import {
    projectState,
    resetProjectState,
} from "@app/store/projectStore.svelte.js";
import { updateDuration } from "@app/store/playerStore.svelte.js";
import {
    resetMarkerState,
    resetMarkerStateWithData,
} from "@app/store/markerStore.svelte.js";
import { setIssues } from "@app/store/issueStore.svelte.js";
import { speakerState } from "@app/store/speakerStore.svelte.js";
import { metadataState } from "@app/store/metadataStore.svelte.js";
import { styleState } from "@app/store/styleStore.svelte.js";
import { externalDataState } from "@app/store/externalDataStore.svelte.js";
import {
    establishHistoryBaseline,
} from "@app/store/historyStore.svelte.js";
import frameRates from "@cclib/dict/frameRates.js";
import {
    eventGroupState,
    resetEventGroupState,
} from "@app/store/eventGroupStore.svelte.js";
import { backfillAudioRenderHashes } from "@app/lib/audio/audioRenderState.js";
import tcLib from "@cclib/lib/timecode.js";
import { onMount, tick } from "svelte";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import * as m from "@app/paraglide/messages.js";
import { startProxyRt, stopProxyRt } from "@app/lib/proxyRt.js";

import _Event from "@cclib/classes/event.js";
import _EventGroup from "@cclib/classes/eventGroup.js";
import _Project from "@cclib/classes/project.js";

/** @type {{projectFilePath: any}} */
let { projectFilePath } = $props();
const uid = uuidv4();

let progress = $state(0),
    statusMsg = $state(m.load_project_file_status_starting()),
    audioExtractionComplete = false,
    manifestFileExists = false,
    audioExtractionWorker = environment.electron
        ? new Worker("./build/workers/audioExtraction.js")
        : null;

onMount(async () => {
    setTimeout(() => {
        if (projectFilePath) {
            statusMsg = m.load_project_file_status_read_path();
            readProjectFileFromPath();
        } else if (externalDataState.platform === "Project File") {
            statusMsg = m.load_project_file_status_import_external();
            importProjectFile(JSON.parse(externalDataState.rawDescription));
        } else {
            hideModal();
        }
    }, 0);

    return () => {
        // Cleanup if needed
        if (audioExtractionWorker) {
            audioExtractionWorker.terminate();
        }
    };
});

function checkFileExistsSync(filePath) {
    try {
        window.fsSync.accessSync(filePath);
        return true;
    } catch (error) {
        return false;
    }
}

async function getFileMetadata(filePath, ffmpegFullPath) {
    try {
        const exec = window.exec;
        const result = await exec(
            `"${ffmpegFullPath.replace("app.asar", "app.asar.unpacked")}" -y -i "${filePath}"`,
        );

        return result;
    } catch (err) {
        return err.message;
    }
}

function getMediaIncode(ffmpegOutput) {
    let matches = ffmpegOutput.match(
        /(?<=timecode\s+:\s+)\d\d:\d\d:\d\d(:|;)\d\d/,
    );
    let mediaIncode = matches ? matches[0].replace(";", ":") : false;

    console.log("Media incode detected: " + mediaIncode);
    return mediaIncode == "null" ? false : mediaIncode;
}

function getMediaFrameRate(ffmpegOutput) {
    let matches = ffmpegOutput.match(/\d+\.\d+(?= fps)|\d+(?= fps)/);
    let mediaFrameRate = matches ? matches[0] : false;

    console.log("Media frame rate detected: " + mediaFrameRate);
    let fr = frameRates.frameRateMapping[mediaFrameRate];
    return fr;
}

function getMediaDuration(ffmpegOutput) {
    let matches = ffmpegOutput.match(/(?<=Duration:\s)\d\d:\d\d:\d\d\.\d\d/);
    let mediaDuration = matches ? matches[0] : false;

    console.log(mediaDuration);
    if (mediaDuration) {
        mediaDuration = tcLib.tcMsToSec(mediaDuration);
        console.log("media duration in seconds: " + mediaDuration);
    }

    return mediaDuration;
}

function readProjectFileFromPath() {
    progress = 10;
    statusMsg = m.load_project_file_status_reading();
    window.fsSync.readFile(projectFilePath, (err, data) => {
        if (err) {
            console.log(err);
            toast.push(
                m.load_project_file_read_failed_toast({ error: err.message }),
                {
                    classes: ["toast-danger"],
                },
            );
            hideModal();
        } else {
            const projectJson = JSON.parse(data);
            importProjectFile(projectJson);
        }
    });
}

async function importProjectFile(projectJson) {
    try {
        progress = 15;
        statusMsg = m.load_project_file_status_importing();
        player.src = '';
        /* Reset States */
        resetEventGroupState();
        await tick();
        metadataState.length = 0;
        speakerState.length = 0;
        setIssues([]);
        resetMarkerState();
        await tick();
        resetProjectState({
            ...projectJson,
            ...{
                eventGroups: [],
                metadata: [],
                speakers: [],
                issues: [],
                markers: {},
                style: {},
                folderId: externalDataState.folderId || projectJson.folderId,
                localPath: projectFilePath ? projectFilePath.replace(/"/g, "") : null,
                useLocalStorage: projectFilePath ? true : false,
            },
        });

        await tick();

        projectState.id = projectJson.id;
        projectJson.eventGroups.forEach(async (eventGroup, eventGroupIndex) => {
            // Create a new event group and add it to the state using push pattern
            const newEventGroup = {
                ...new _EventGroup({
                    ...eventGroup,
                    events: [],
                }),
            };

            newEventGroup.id = eventGroup.id;

            // Push to array instead of reassigning
            eventGroupState.push(newEventGroup);
            setTimeout(() => {
                eventGroupState[eventGroupIndex].events = eventGroup.events.map(
                    (event) => {
                        return {
                            ...(new _Event({...event})),
                            id : event.id
                        };
                    }
                );
                backfillAudioRenderHashes([eventGroupState[eventGroupIndex]]);
            }, 250);
        });

        progress = 25;
        statusMsg = m.load_project_file_status_metadata();

        await tick();

        if (projectJson.metadata) {
            projectJson.metadata.forEach((metadataInfo) => {
                metadataState.push(metadataInfo);
            });
        }

        await tick();

        if (projectJson.speakers) {
            projectJson.speakers.forEach((speakerInfo) => {
                speakerState.push(speakerInfo);
            });
        }

        await tick();

        if (projectJson.issues && projectJson.issues.length > 0) {
            setIssues(projectJson.issues);
        }

        await tick();
        resetMarkerStateWithData(
            projectJson.markers || {
                selected: 0,
                lists: [
                    {
                        id: "0",
                        name: "Shot Changes",
                        color: "#E74C3C",
                        markers: [],
                    },
                ],
            },
        );

        await tick();

        /* Import Style Info */
        if (projectJson.style) {
            for (const [key, value] of Object.entries(projectJson.style)) {
                styleState[key] = value;
            }
        }

        if (styleState.lineSpacing > 5) {
            styleState.lineSpacing = 1;
        }

        if (eventGroupState.length > 0) {
            projectState.selected = eventGroupState.length - 1;
        }

        progress = 45;
        statusMsg = m.load_project_file_status_importing_media();
        establishHistoryBaseline({ name: "load project" });
        await tick();

        /* Blob URLs saved by the machine that created the project are dead on
           reload. Local media plays from localPath, so drop stale blob:
           references (real URLs for Cloud/streaming sources are preserved). */
        if (typeof projectState.media?.path === "string" && projectState.media.path.startsWith("blob:")) {
            projectState.media.path = "";
        }
        if (typeof projectState.media?.audioPath === "string" && projectState.media.audioPath.startsWith("blob:")) {
            projectState.media.audioPath = "";
        }
        /* Proxy RT playback URLs point at a loopback media server whose port
           changes between app launches — always regenerate on load. */
        if (
            projectState.media?.storage === "Proxy RT" &&
            typeof projectState.media?.path === "string" &&
            /^https?:\/\/127\.0\.0\.1/.test(projectState.media.path)
        ) {
            projectState.media.path = "";
        }

        if (
            ((projectJson.media.storage === "Local Storage" ||
                projectJson.media.storage === "Proxy RT") &&
                !environment.electron) ||
            projectJson.media.localPath === ""
        ) {
            //ALERT USER TO IMPORT MEDIA USING SWAL
            
            await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.ok,
                title: m.load_project_file_media_import_required_title(),
                text: m.load_project_file_media_import_required_text(),
                icon: "warning",
                confirmButtonText: m.ok(),
            });

            player.src = '';
            showModal("mediaImport");
        } else {
            await tick();            
            importMedia();
        }
    } catch (err) {
        
        console.log(err);
        toast.push(
            m.load_project_file_project_import_failed_toast({ error: err.msg }),
            {
                classes: ["toast-danger"],
            },
        );
        hideModal();
    } finally {
        externalDataState.rawDescription = false;
    }
}

async function importMedia() {
    try {
        updateDuration(false);
        uiState.timeline = false;

        /* Kill any Proxy RT session from previously loaded media. */
        if (environment.electron) {
            stopProxyRt();
        }
        let mediaType = "video/mp4"; //default
        let audioOutputPath;
        switch (projectState.media.storage) {
            case "Vimeo":
                progress = 75;
                statusMsg = m.load_project_file_status_loading_vimeo();
                
                player.src = projectState.media.path;
                manifestFileExists = true;
                audioExtractionComplete = true;
                projectState.media.useFallback = true;
                break;
            case "YouTube":
                progress = 75;
                statusMsg = m.load_project_file_status_loading_youtube();
                
                player.src = projectState.media.path;
                manifestFileExists = true;
                audioExtractionComplete = true;
                projectState.media.useFallback = true;
                break;
            case "Mux":
            case "DASH Manifest":
            case "Cloudflare Stream":
                progress = 75;
                statusMsg = m.load_project_file_status_loading_stream();

                player.src = projectState.media.path;
                manifestFileExists = true;
                audioExtractionComplete = true;
                projectState.media.useFallback = true;
                break;
            case "HLS Manifest":
                progress = 75;
                statusMsg = m.load_project_file_status_loading_hls();
                
                audioExtractionComplete = true;
                player.src = projectState.media.path;
                break;
            case "Proxy RT":
                progress = 75;
                statusMsg = m.load_project_file_status_generating_proxy();
                
                if (!checkFileExistsSync(projectState.media.localPath)) {
                    toast.push(
                        m.load_project_file_media_not_found_toast(),
                        {
                            classes: ["toast-danger"],
                        },
                    );
                    showModal("mediaImport");
                    return;
                }

                audioOutputPath =
                    window.os.tmpdir() + window.path.sep + uid + ".json";

                let ffmpegRes = await getFileMetadata(
                    projectState.media.localPath,
                    window.ffmpegPath,
                );
                
                projectState.media.info = {
                    incode: getMediaIncode(ffmpegRes),
                    frameRate: getMediaFrameRate(ffmpegRes),
                    duration: getMediaDuration(ffmpegRes),
                };
                
                audioExtractionWorker.onmessage = (msg) => {
                    if (msg.data.status === "in_progress") {
                        statusMsg = msg.data.result;
                    } else {
                        
                        projectState.media.peaksPath = msg.data.error
                            ? ""
                            : audioOutputPath;
                        projectState.media.useFallback = false;

                        toast.push(
                            `${msg.data.error ? msg.data.error : m.load_project_file_audio_extraction_complete_toast()}`,
                            {
                                classes: [
                                    `toast-${msg.data.error ? "danger" : "success"}`,
                                ],
                            },
                        );

                        audioExtractionComplete = true;
                        closeModal();
                    }
                };

                audioExtractionWorker.postMessage({
                    inputPath: projectState.media.localPath,
                    outputPath: audioOutputPath,
                    ffmpegPath: window.ffmpegPath,
                    duration: projectState.media.info.duration,
                });

                try {
                    const proxySession = await startProxyRt({
                        inputPath: projectState.media.localPath,
                        duration: projectState.media.info.duration,
                        onProgress: (proxyProgress) => {
                            statusMsg = `Generating Proxy Stream: ${proxyProgress.percent}% (${proxyProgress.segmentsCompleted}/${proxyProgress.totalSegments} segments)`;
                        },
                    });

                    projectState.media.path = proxySession.url;
                    manifestFileExists = true;
                    player.src = proxySession.url;
                    closeModal();
                } catch (err) {
                    manifestFileExists = true;
                    toast.push(`${err.message}`, {
                        classes: ["toast-danger"],
                    });

                    closeModal();
                }

                break;
            case "Cloud Storage":
                /* Cloud Storage*/
                
                projectState.media.useFallback = true;
                if (projectState.media.type === "video/quicktime") {
                    mediaType = "video/mp4";
                } else if (projectState.media.type === "video/x-matroska") {
                    mediaType = "video/mkv";
                } else {
                    mediaType = projectState.media.type;
                }

                
                //Get file size of url
                let fileSize = await getFileSize(projectState.media.path);
                if (fileSize) {
                    const sizeInBytes = parseInt(fileSize);
                    const sizeInKilobytes = sizeInBytes / 1024;
                    const sizeInMegabytes = sizeInKilobytes / 1024;
                    
                    if (sizeInMegabytes < 512) {
                        projectState.media.useFallback = false;
                    }
                }

                player.src = { src: projectState.media.path, type: mediaType };

                /* Update Flags */
                manifestFileExists = true;
                audioExtractionComplete = true;

                break;
            default:
                if (!checkFileExistsSync(projectState.media.localPath)) {
                    toast.push(
                        m.load_project_file_media_not_found_toast(),
                        {
                            classes: ["toast-danger"],
                        },
                    );
                    showModal("mediaImport");
                    return;
                }

                /* Local Storage */
                audioOutputPath =
                    window.os.tmpdir() + window.path.sep + uid + ".json";
                mediaType =
                    !projectState.media.type ||
                    projectState.media.type === "video/quicktime"
                        ? "video/mp4"
                        : projectState.media.type;

                player.src = {
                    src: projectState.media.localPath,
                    type: mediaType,
                };

                progress = 75;
                manifestFileExists = true;
                statusMsg = m.load_project_file_status_generating_audio_preview();
                

                audioExtractionWorker.onmessage = (msg) => {
                    if (msg.data.status === "in_progress") {
                        statusMsg = msg.data.result;
                    } else if (msg.data.error) {
                        projectState.media.peaksData = [[0],[0]];
                        audioExtractionComplete = true;
                        closeModal();
                    } else {                        
                        projectState.media.peaksPath = msg.data.error
                            ? ""
                            : audioOutputPath;
                        projectState.media.useFallback = false;

                        toast.push(
                            `${msg.data.error ? msg.data.error : m.load_project_file_media_import_complete_toast()}`,
                            {
                                classes: [
                                    `toast-${msg.data.error ? "danger" : "success"}`,
                                ],
                            },
                        );

                        audioExtractionComplete = true;
                        closeModal();
                    }
                };

                audioExtractionWorker.postMessage({
                    inputPath: projectState.media.localPath,
                    outputPath: audioOutputPath,
                    ffmpegPath: window.ffmpegPath,
                    duration: projectState.media.info.duration,
                });
        }

        closeModal();
    } catch (err) {
        
        console.error(err, err.message);
        toast.push(m.load_project_file_media_import_failed_toast(), {
            classes: ["toast-danger"],
        });

        showModal("mediaImport");
    }
}

function closeModal() {
    if (audioExtractionComplete && manifestFileExists) {
        statusMsg = m.load_project_file_status_complete();
        progress = 100;

        setTimeout(() => {            
            uiState.timeline = true;
            hideModal();
        }, 1000);
    }
}

async function getFileSize() {    
    try {
        const response = await fetch(projectState.media.path, {
            method: "HEAD",
        });
        const fileSize = response.headers.get("content-length");
        return fileSize;
    } catch (err) {
        
        return false;
    }
}
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'loadProjectFile'
        ? 'show d-block'
        : ''}"
    role="dialog"
    aria-labelledby="loadProjectTitle"
    aria-describedby="loadProjectStatus"
    tabindex="-1"
    id="LoadProjectFileModal">
    <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="loadProjectTitle">
                    {m.load_project_file_title()}
                </h4>
                <button
                    type="button"
                    class="btn-close"
                    id="btnCloseProjectLoad"
                    aria-label={m.load_project_file_close_label()}
                    onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                <p id="loadProjectStatus" aria-live="polite">
                    <i class="bi bi-exclamation-diamond-fill" aria-hidden="true"
                    ></i>
                    {m.load_project_file_progress_label()} | {statusMsg}
                </p>
                <div
                    class="progress"
                    role="progressbar"
                    aria-valuenow={progress}
                    aria-valuemin="0"
                    aria-valuemax="100">
                    <div
                        class="progress-bar bg-primary progress-bar-striped progress-bar-animated"
                        style="width: {progress}%;">
                        {progress}%
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
